import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getEdges } from "../api/edgeApi";
import {
  getAlarmRules,
  deleteAlarmRule,
  toggleAlarmRule,
  CONDITION_TYPES,
  SENSOR_FIELDS,
} from "../api/alarmRuleApi";
import AlarmRuleForm from "../components/AlarmRules/AlarmRuleForm";

export default function AlarmList() {
  const accessToken = useSelector((s) => s.session?.accessToken);

  const [rules, setRules] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEdgeId, setSelectedEdgeId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);

  // Edge listesini yükle
  useEffect(() => {
    if (!accessToken) return;

    async function fetchEdges() {
      try {
        const data = await getEdges(accessToken);
        setEdges(data);
      } catch (err) {
        console.error("Edge listesi alinamadi:", err);
      }
    }

    fetchEdges();
  }, [accessToken]);

  // Alarm kurallarını yükle
  const fetchRules = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const data = await getAlarmRules(accessToken, selectedEdgeId || null);
      setRules(data);
    } catch (err) {
      console.error("Alarm kurallari alinamadi:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, selectedEdgeId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const getConditionLabel = (condition) => {
    const ct = CONDITION_TYPES.find((c) => c.value === condition);
    return ct ? ct.symbol : condition;
  };

  const getFieldLabel = (field) => {
    const sf = SENSOR_FIELDS.find((s) => s.value === field);
    return sf ? `${sf.label} (${sf.unit})` : field;
  };

  const getEdgeName = (edgeId) => {
    const edge = edges.find((e) => e.id === edgeId);
    return edge ? edge.edgeId : edgeId;
  };

  const handleEditRule = (rule) => {
    setEditRule(rule);
    setModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Bu kurali silmek istediginize emin misiniz?")) return;

    try {
      await deleteAlarmRule(accessToken, ruleId);
      fetchRules();
    } catch (err) {
      console.error("Kural silinemedi:", err);
      alert("Kural silinirken hata olustu");
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await toggleAlarmRule(accessToken, ruleId);
      fetchRules();
    } catch (err) {
      console.error("Kural durumu degistirilemedi:", err);
      alert("Kural durumu degistirilirken hata olustu");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditRule(null);
    fetchRules();
  };

  const parseActions = (actionsStr) => {
    if (!actionsStr) return [];
    try {
      return JSON.parse(actionsStr);
    } catch {
      return [];
    }
  };

  // eslint-disable-next-line no-unused-vars -- ileride aksiyon formatı için
  const formatActions = (actions) => {
    if (!actions || actions.length === 0) return "-";
    return actions.map((a) => a.type).join(", ");
  };

  if (loading && rules.length === 0) {
    return <div style={styles.container}>Yukleniyor...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Alarmlar</h1>
        <div style={styles.headerActions}>
          {/* Edge Filter */}
          <select
            style={styles.select}
            value={selectedEdgeId}
            onChange={(e) => setSelectedEdgeId(e.target.value)}
          >
            <option value="">Tum Edge'ler</option>
            {edges.map((edge) => (
              <option key={edge.id} value={edge.id}>
                {edge.edgeId}
              </option>
            ))}
          </select>

          <button
            style={styles.addRuleBtn}
            onClick={() => {
              setEditRule(null);
              setModalOpen(true);
            }}
          >
            + Yeni Kural Ekle
          </button>
        </div>
      </div>

      {/* Alarm Kuralları */}
        <div style={styles.tableWrapper}>
          {rules.length === 0 ? (
            <div style={styles.emptyState}>
              <p>Henuz alarm kurali tanimlanmamis.</p>
              <button
                style={styles.addRuleBtn}
                onClick={() => {
                  setEditRule(null);
                  setModalOpen(true);
                }}
              >
                + Ilk Kurali Olustur
              </button>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Durum</th>
                  <th style={styles.th}>Kural Adi</th>
                  <th style={styles.th}>Edge</th>
                  <th style={styles.th}>Kosul</th>
                  <th style={styles.th}>Aksiyonlar</th>
                  <th style={styles.th}>Kontrol</th>
                  <th style={styles.th}>Islemler</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: rule.is_active ? "#238636" : "#6c757d",
                        }}
                      >
                        {rule.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.ruleName}>{rule.name}</div>
                    </td>
                    <td style={styles.td}>{getEdgeName(rule.edge_id)}</td>
                    <td style={styles.td}>
                      <code style={styles.condition}>
                        {getFieldLabel(rule.field)} {getConditionLabel(rule.condition)}{" "}
                        {rule.threshold}
                        {rule.condition === "BETWEEN" && ` - ${rule.threshold2}`}
                      </code>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBadges}>
                        {parseActions(rule.actions).map((action, idx) => (
                          <span key={idx} style={styles.actionBadge}>
                            {action.type}
                          </span>
                        ))}
                        {parseActions(rule.actions).length === 0 && "-"}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.intervalBadge}>
                        {rule.check_interval_seconds}sn / {rule.cooldown_minutes}dk
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => handleToggleRule(rule.id)}
                          title={rule.is_active ? "Devre Disi Birak" : "Aktifles"}
                        >
                          {rule.is_active ? "Durdur" : "Baslat"}
                        </button>
                        <button
                          style={styles.actionBtn}
                          onClick={() => handleEditRule(rule)}
                        >
                          Duzenle
                        </button>
                        <button
                          style={{ ...styles.actionBtn, color: "#f85149" }}
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {/* Alarm Rule Form Modal */}
      {modalOpen && (
        <AlarmRuleForm
          edgeDbId={selectedEdgeId || (edges[0]?.id || "")}
          edges={edges}
          rule={editRule}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    color: "#e6edf3",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  select: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "14px",
    cursor: "pointer",
  },
  addRuleBtn: {
    backgroundColor: "#238636",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "500",
  },
  tabs: {
    display: "flex",
    gap: "4px",
    marginBottom: "20px",
    borderBottom: "1px solid #30363d",
  },
  tab: {
    backgroundColor: "transparent",
    color: "#8b949e",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "12px 20px",
    fontSize: "14px",
    cursor: "pointer",
    marginBottom: "-1px",
  },
  tabActive: {
    backgroundColor: "transparent",
    color: "#e6edf3",
    border: "none",
    borderBottom: "2px solid #238636",
    padding: "12px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "500",
    marginBottom: "-1px",
  },
  filters: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  filterBtn: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    cursor: "pointer",
  },
  filterActive: {
    backgroundColor: "#238636",
    color: "#fff",
    border: "1px solid #238636",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    cursor: "pointer",
  },
  tableWrapper: {
    backgroundColor: "#161b22",
    borderRadius: "12px",
    border: "1px solid #30363d",
    overflow: "hidden",
  },
  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    color: "#8b949e",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    color: "#8b949e",
    fontWeight: "500",
    fontSize: "13px",
    borderBottom: "1px solid #30363d",
    backgroundColor: "#0d1117",
  },
  tr: {
    borderBottom: "1px solid #21262d",
  },
  td: {
    padding: "14px 16px",
    fontSize: "14px",
  },
  ruleName: {
    fontWeight: "500",
    color: "#e6edf3",
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#fff",
  },
  ackBtn: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "4px",
    padding: "6px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  condition: {
    backgroundColor: "#21262d",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "13px",
    color: "#ff9800",
  },
  actionBadges: {
    display: "flex",
    gap: "4px",
    flexWrap: "wrap",
  },
  actionBadge: {
    backgroundColor: "#1f6feb33",
    color: "#58a6ff",
    padding: "4px 8px",
    borderRadius: "8px",
    fontSize: "11px",
    fontWeight: "500",
  },
  intervalBadge: {
    backgroundColor: "#21262d",
    color: "#8b949e",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
  },
  actions: {
    display: "flex",
    gap: "8px",
  },
  actionBtn: {
    background: "none",
    border: "none",
    color: "#58a6ff",
    fontSize: "13px",
    cursor: "pointer",
    padding: "4px",
  },
};
